/**
 * WeekDetail — full view of one week's training summary at /weeks/:iso.
 *
 * Renders:
 *   1. Header (date range + Back / Regenerate)
 *   2. Stats panel (totals, CTL/ATL/TSB endpoints, zone time, sports)
 *   3. Safety flag pills (if any fired)
 *   4. Coach analysis (three-section markdown, rendered with explicit headers)
 *   5. Vs last week comparison table
 *
 * Empty weeks (zero rides) render a "Rest week — nothing to analyze"
 * placeholder and hide the generate button. Missing-summary case
 * shows a generate button in place of the analysis text.
 */
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGenerateWeeklySummary,
  useWeeklySummary,
} from '../hooks/useWeeklySummary.ts';
import { GarminAiAttribution, RideAttribution } from '../components/GarminAttribution.tsx';
import { SafetyFlagPill } from '../components/SafetyFlagPill.tsx';
import type { WeeklyStats, WeeklySummary } from '../types/api.ts';

function isoMonday(iso: string | undefined): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  // 1 == Monday in UTC.
  return d.getUTCDay() === 1 ? iso : null;
}

function prevMonday(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 7);
  return d.toISOString().slice(0, 10);
}

function formatDateRange(start: string, end: string): string {
  const fmt = (s: string) =>
    new Date(`${s}T00:00:00Z`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  return `${fmt(start)} – ${fmt(end)}`;
}

interface ParsedSection {
  heading: string;
  body: string;
}

function parseSummarySections(summary: string): ParsedSection[] {
  const pattern = /^##[ \t]+(\S.*)$/gm;
  const headers: Array<{ heading: string; start: number; bodyStart: number }> = [];
  let match;
  while ((match = pattern.exec(summary)) !== null) {
    headers.push({
      heading: match[1].trim(),
      start: match.index,
      bodyStart: match.index + match[0].length,
    });
  }
  if (headers.length === 0) {
    return [{ heading: '', body: summary.trim() }];
  }
  return headers.map((h, i) => {
    const end = i + 1 < headers.length ? headers[i + 1].start : summary.length;
    return { heading: h.heading, body: summary.slice(h.bodyStart, end).trim() };
  });
}

interface DeltaCell {
  label: string;
  cur: number;
  prev: number;
  fmtCur: string;
  fmtPrev: string;
  fmtDelta: string;
}

function buildComparison(
  cur: WeeklyStats,
  prev: WeeklyStats | undefined,
): DeltaCell[] | null {
  if (!prev) return null;
  const fmt = (n: number, suffix: string) =>
    `${Number.isInteger(n) ? n : n.toFixed(1)}${suffix}`;
  const fmtDelta = (d: number, suffix: string) => {
    const rounded = Math.abs(d) >= 10 ? Math.round(d) : Number(d.toFixed(1));
    const sign = rounded >= 0 ? '+' : '';
    return `${sign}${rounded}${suffix}`;
  };
  return [
    {
      label: 'TSS',
      cur: cur.totals.tss,
      prev: prev.totals.tss,
      fmtCur: fmt(cur.totals.tss, ''),
      fmtPrev: fmt(prev.totals.tss, ''),
      fmtDelta: fmtDelta(cur.totals.tss - prev.totals.tss, ''),
    },
    {
      label: 'Hours',
      cur: cur.totals.hours,
      prev: prev.totals.hours,
      fmtCur: fmt(cur.totals.hours, 'h'),
      fmtPrev: fmt(prev.totals.hours, 'h'),
      fmtDelta: fmtDelta(cur.totals.hours - prev.totals.hours, 'h'),
    },
    {
      label: 'Distance',
      cur: cur.totals.km,
      prev: prev.totals.km,
      fmtCur: fmt(cur.totals.km, ' km'),
      fmtPrev: fmt(prev.totals.km, ' km'),
      fmtDelta: fmtDelta(cur.totals.km - prev.totals.km, ' km'),
    },
    {
      label: 'TSB end',
      cur: cur.tsb_end,
      prev: prev.tsb_end,
      fmtCur: fmt(cur.tsb_end, ''),
      fmtPrev: fmt(prev.tsb_end, ''),
      fmtDelta: fmtDelta(cur.tsb_end - prev.tsb_end, ''),
    },
  ];
}

export function WeekDetail() {
  const { t } = useTranslation();
  const { iso } = useParams<{ iso: string }>();
  const monday = useMemo(() => isoMonday(iso), [iso]);
  const prevIso = useMemo(() => (monday ? prevMonday(monday) : null), [monday]);

  const cur = useWeeklySummary(monday);
  const prev = useWeeklySummary(prevIso);
  const generate = useGenerateWeeklySummary();

  if (!monday) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <h1 className="text-lg font-semibold text-(--color-text-primary)">
          {t('weekDetail.invalidIso')}
        </h1>
        <Link
          to="/rides"
          className="text-emerald-400 hover:text-emerald-300 text-sm mt-2 inline-block"
        >
          ← {t('weekDetail.backToCalendar')}
        </Link>
      </div>
    );
  }

  if (cur.isLoading || !cur.data) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="h-6 w-48 rounded-lg bg-(--color-bg-tertiary) animate-pulse" />
        <div className="h-24 mt-4 rounded-xl bg-(--color-bg-tertiary) animate-pulse" />
      </div>
    );
  }

  const data: WeeklySummary = cur.data;
  const stats = data.stats;
  const isEmpty = stats.totals.rides === 0;
  const sections = data.summary ? parseSummarySections(data.summary) : null;
  const comparison = isEmpty ? null : buildComparison(stats, prev.data?.stats);
  const isGenerating =
    generate.isPending && generate.variables === monday;
  const generateLabel = data.summary
    ? t('weekDetail.regenerate')
    : t('weekDetail.generate');

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1 min-w-0">
          <Link
            to="/rides"
            className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-xs"
          >
            ← {t('weekDetail.backToCalendar')}
          </Link>
          <h1 className="text-lg sm:text-xl font-semibold text-(--color-text-primary)">
            {t('weekDetail.weekOf', {
              range: formatDateRange(data.week_start, data.week_end),
            })}
          </h1>
          <RideAttribution text={data.data_sources?.join(', ')} />
        </div>
        {!isEmpty && (
          <button
            type="button"
            onClick={() => generate.mutate(monday)}
            disabled={isGenerating}
            className="text-xs font-medium border border-emerald-400/40 hover:border-emerald-300/60 text-emerald-400 hover:text-emerald-300 disabled:opacity-50 rounded-full px-3 py-1.5 transition-colors"
          >
            {isGenerating ? t('weekDetail.regenerating') : generateLabel}
          </button>
        )}
      </div>

      {isEmpty && (
        <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-6 text-center text-(--color-text-secondary)">
          <p className="font-medium text-(--color-text-primary)">
            {t('weekDetail.restWeekTitle')}
          </p>
          <p className="text-sm mt-1">{t('weekDetail.restWeekBody')}</p>
        </div>
      )}

      {!isEmpty && <StatsPanel stats={stats} />}

      {!isEmpty && data.flags.length > 0 && (
        <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-2">
          <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
            {t('weekDetail.flagsTitle')}
          </h2>
          <div className="flex flex-wrap gap-2">
            {data.flags.map((f) => (
              <SafetyFlagPill key={f.id} flag={f} />
            ))}
          </div>
        </div>
      )}

      {!isEmpty && (
        <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3">
          <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
            {t('weekDetail.analysisTitle')}
          </h2>
          {sections ? (
            <div className="flex flex-col gap-4">
              {sections.map((s, i) => (
                <div key={`${i}-${s.heading}`} className="flex flex-col gap-1">
                  {s.heading && (
                    <h3 className="text-base font-semibold text-(--color-text-primary)">
                      {s.heading}
                    </h3>
                  )}
                  <p className="text-sm text-(--color-text-primary) leading-relaxed whitespace-pre-wrap">
                    {s.body}
                  </p>
                </div>
              ))}
              <GarminAiAttribution sources={data.ai_sources} />
              {data.summary_at && (
                <p className="text-[10px] text-(--color-text-secondary)">
                  {t('weekDetail.generatedAt', {
                    when: new Date(data.summary_at).toLocaleString(),
                  })}
                  {data.prompt_version && ` · ${data.prompt_version}`}
                </p>
              )}
            </div>
          ) : (
            <div className="text-sm text-(--color-text-secondary)">
              {t('weekDetail.notGeneratedYet')}
            </div>
          )}
        </div>
      )}

      {comparison && (
        <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-2">
          <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
            {t('weekDetail.comparisonTitle')}
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-(--color-text-secondary) text-xs">
                <th className="text-left font-normal py-1"> </th>
                <th className="text-right font-normal py-1">
                  {t('weekDetail.thisWeek')}
                </th>
                <th className="text-right font-normal py-1">
                  {t('weekDetail.lastWeek')}
                </th>
                <th className="text-right font-normal py-1">Δ</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map((row) => (
                <tr
                  key={row.label}
                  className="border-t border-(--color-border)/40"
                >
                  <td className="py-1.5 text-(--color-text-secondary)">
                    {row.label}
                  </td>
                  <td className="py-1.5 text-right font-medium text-(--color-text-primary)">
                    {row.fmtCur}
                  </td>
                  <td className="py-1.5 text-right text-(--color-text-secondary)">
                    {row.fmtPrev}
                  </td>
                  <td
                    className={`py-1.5 text-right font-medium ${
                      row.cur - row.prev >= 0
                        ? 'text-emerald-400'
                        : 'text-amber-400'
                    }`}
                  >
                    {row.fmtDelta}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatsPanel({ stats }: Readonly<{ stats: WeeklyStats }>) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3">
      <h2 className="text-sm font-medium text-(--color-text-secondary) uppercase tracking-wide">
        {t('weekDetail.statsTitle')}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <StatTile label={t('weekDetail.rides')} value={`${stats.totals.rides}`} />
        <StatTile label={t('weekDetail.hours')} value={`${stats.totals.hours.toFixed(1)}h`} />
        <StatTile label={t('weekDetail.distance')} value={`${stats.totals.km} km`} />
        <StatTile label="TSS" value={`${stats.totals.tss}`} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-sm">
        <StatTile
          label="CTL"
          value={`${stats.ctl_start} → ${stats.ctl_end}`}
          delta={stats.ctl_end - stats.ctl_start}
        />
        <StatTile
          label="ATL"
          value={`${stats.atl_start} → ${stats.atl_end}`}
          delta={stats.atl_end - stats.atl_start}
        />
        <StatTile label="TSB end" value={`${stats.tsb_end}`} />
      </div>
      {stats.zone_time_h && (
        <div>
          <p className="text-xs text-(--color-text-secondary) mb-1">
            {t('weekDetail.zoneTime')}
          </p>
          <div className="flex flex-wrap gap-2 text-xs">
            {(['z1', 'z2', 'z3', 'z4', 'z5'] as const).map((z) => (
              <span
                key={z}
                className="rounded-md bg-(--color-bg-tertiary) border border-(--color-border) px-2 py-0.5 text-(--color-text-primary)"
              >
                {z.toUpperCase()} {stats.zone_time_h?.[z].toFixed(1)}h
              </span>
            ))}
          </div>
        </div>
      )}
      {Object.keys(stats.sport_counts).length > 1 && (
        <div className="text-xs text-(--color-text-secondary)">
          {t('weekDetail.sports')}:{' '}
          {Object.entries(stats.sport_counts)
            .map(([sport, count]) => `${sport} ${count}`)
            .join(' · ')}
        </div>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  delta,
}: Readonly<{ label: string; value: string; delta?: number }>) {
  return (
    <div className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) p-2">
      <div className="text-[10px] text-(--color-text-secondary) uppercase tracking-wide">
        {label}
      </div>
      <div className="text-(--color-text-primary) font-semibold text-sm">
        {value}
      </div>
      {delta !== undefined && (
        <div
          className={`text-[10px] ${
            delta >= 0 ? 'text-emerald-400' : 'text-amber-400'
          }`}
        >
          Δ {delta >= 0 ? '+' : ''}
          {Math.round(delta * 10) / 10}
        </div>
      )}
    </div>
  );
}
